import { BuildingOfficeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { DealStatusEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'primitives/Button';

import constants from 'utils/constants';
import { toTitleCase } from 'utils/format-helper';

interface LockedBasketsViewProps {
  recommendedDeals: any[];
}

const DealCard: FC<{
  deal: any;
  industries: string[];
  description: string;
  navigate: (path: string) => void;
}> = ({ deal, industries, description, navigate }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [imageError, setImageError] = useState(false);
  const company = deal.companyInformation?.company;
  const hasImage = company?.image && !imageError;
  const isFunded =
    deal.status !== DealStatusEnumType.Raising && deal.status !== DealStatusEnumType.Pooling;

  if (isFunded) {
    return (
      <div
        className="rounded-2xl p-5 bg-slate-700 border border-slate-600 w-full relative overflow-hidden min-h-[280px] flex flex-col shadow-lg"
        style={{
          background: 'linear-gradient(135deg, rgb(51 65 85) 0%, rgb(30 41 59) 100%)',
        }}
      >
        <div className="relative z-10 flex flex-col flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-slate-700 border border-slate-600 overflow-hidden flex-shrink-0">
              {hasImage ? (
                <img
                  src={company?.image || constants.ALT_AVATAR_URL}
                  alt={company?.name || 'Company'}
                  className="w-full h-full object-contain p-2"
                  onError={() => setImageError(true)}
                />
              ) : (
                <BuildingOfficeIcon className="h-6 w-6 text-slate-300" />
              )}
            </div>
            <div className="flex flex-col items-center gap-2">
              <p className="text-lg font-semibold text-white text-center">
                {company?.name || 'Undisclosed Company'}
              </p>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">
                FUNDED
              </span>
            </div>
          </div>
          {description && (
            <p className="text-sm text-slate-200 leading-relaxed mt-4 text-center">{description}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full"
      style={{ perspective: '1000px', minHeight: '280px' }}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <div
        className="relative w-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front of card - Locked state (White/Blue) */}
        <div
          className="rounded-2xl p-6 bg-white border border-indigo-200 shadow-lg relative w-full min-h-[280px] flex flex-col"
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          {/* Company logo and name */}
          <div className="flex items-start gap-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-white border border-slate-200 overflow-hidden flex-shrink-0 filter blur-sm select-none opacity-50">
              {hasImage ? (
                <img
                  src={company.image || constants.ALT_AVATAR_URL}
                  alt={company?.name || 'Company'}
                  className="w-full h-full object-contain p-2"
                  onError={() => setImageError(true)}
                />
              ) : (
                <BuildingOfficeIcon className="h-6 w-6 text-slate-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-lg font-semibold text-slate-900 filter blur-sm select-none opacity-50">
                {company?.name || 'Undisclosed Company'}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="text-center mb-6 flex-1 flex items-center justify-center">
            <p className="text-slate-900 text-base leading-relaxed">
              {description || 'Investment opportunity available to accredited investors'}
            </p>
          </div>

          {/* Sector tags */}
          {industries && industries.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {industries.slice(0, 2).map((industry, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-sm"
                >
                  {toTitleCase(industry)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Back of card - Hover state (Gray) */}
        <div
          className="rounded-2xl p-6 bg-slate-50 border border-slate-100 absolute inset-0 w-full min-h-[280px] flex flex-col"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {/* Center lock icon */}
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full border-2 border-slate-300">
              <LockClosedIcon className="h-8 w-8 text-slate-400" />
            </div>
          </div>

          {/* Message */}
          <p className="text-slate-900 text-base text-center mb-6 px-4 leading-relaxed">
            This deal is only available to accredited investors
          </p>

          {/* Apply for Access button */}
          <div className="mb-6">
            <Button
              variant="primary"
              className="w-full px-6 py-3 text-base font-semibold"
              onClick={() => navigate('/invest/accreditation')}
            >
              Apply for Access
            </Button>
          </div>

          {/* Sector tags inside box */}
          {industries && industries.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center">
              {industries.slice(0, 2).map((industry, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-lg bg-slate-100 text-slate-700 text-sm"
                >
                  {toTitleCase(industry)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const LockedBasketsView: FC<LockedBasketsViewProps> = ({ recommendedDeals }) => {
  const navigate = useNavigate();

  return (
    <section className="space-y-6 pt-4">
      <div className="space-y-2">
        <p className="text-sm text-slate-500 uppercase tracking-wide">AngelList India</p>
        <h2 className="text-3xl font-semibold text-slate-900">Featured Investment Opportunities</h2>
        <p className="text-slate-500">
          Get accredited to unlock access to these curated investment opportunities.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {recommendedDeals.map(deal => {
          const industries = deal.companyInformation?.industries || [];
          const description = deal.publicData?.description || '';

          return (
            <DealCard
              key={deal.id}
              deal={deal}
              industries={industries}
              description={description}
              navigate={navigate}
            />
          );
        })}
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-6 mt-12 text-center space-y-6">
        <div className="space-y-2">
          <div className="mx-auto w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <LockClosedIcon className="h-6 w-6 text-slate-600" />
          </div>
          <h3 className="text-2xl font-semibold text-slate-900">Unlock Access to Encase</h3>
          <p className="text-slate-500 max-w-xl mx-auto">
            These investment opportunities are available only to accredited investors. Complete your
            accreditation process to view full details and start investing.
          </p>
        </div>
        <Button
          variant="primary"
          className="w-full sm:w-auto px-8 py-3 mx-auto"
          onClick={() => navigate('/invest/accreditation')}
        >
          Unlock Access
        </Button>
      </div>
    </section>
  );
};
